import { LockIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

export const BLOCKED_NOTICE_TEXT = 'Available when assistant finishes';

/** Long enough for the pill's fade-out animation to finish before it unmounts. */
const BLOCKED_NOTICE_FADE_OUT_MS = 200;

interface IAlertBlockedNoticeProps {
  /** Whether the pill is up. It lingers one fade after this drops so its exit can animate. */
  show: boolean;
}

/**
 * The refusal feedback an alert surface floats over a row clicked mid-turn: a
 * lock pill, centered over the surface, that fades in and out. Absolutely
 * positioned, so the host must be `relative`, and rendered at full opacity
 * outside whatever the host dims.
 *
 * The live region is persistent rather than mounted with the pill — screen
 * readers announce content added to an existing region far more reliably than
 * a region that appears already filled.
 */
const AlertBlockedNotice = ({ show }: IAlertBlockedNoticeProps) => {
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => {
    if (show) {
      setIsMounted(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setIsMounted(false), BLOCKED_NOTICE_FADE_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [show]);

  return (
    <div role="status" className="pointer-events-none absolute inset-0 flex items-center justify-center">
      {isMounted && (
        <span
          className={cx(
            'bg-primary border-primary flex items-center gap-1 rounded-full border px-4 py-2 shadow-md',
            show ? 'animate-in fade-in duration-200' : 'animate-out fade-out fill-mode-forwards duration-200'
          )}
        >
          <LockIcon className="text-fg-quaternary size-[18px] shrink-0" />
          <span className="text-primary whitespace-nowrap text-sm font-medium">{BLOCKED_NOTICE_TEXT}</span>
        </span>
      )}
    </div>
  );
};

export default AlertBlockedNotice;
